// src/components/AudioTest.js
import React, { useState, useEffect } from 'react';
import { Card, Button, Row, Col, Alert, Form, Badge } from 'react-bootstrap';
import useAudioToast from '../hooks/useAudioToast';
import audioNotificationService from '../services/audioNotificationService';

const AudioTest = () => {
  const audioToast = useAudioToast();
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [volume, setVolume] = useState(0.8);
  const [testResults, setTestResults] = useState([]);

  useEffect(() => {
    // Load current audio settings
    setAudioEnabled(audioNotificationService.isEnabled);
    setVolume(audioNotificationService.volume);
  }, []);

  const addTestResult = (type, message, success) => {
    setTestResults(prev => [...prev, {
      id: Date.now(),
      type,
      message,
      success,
      timestamp: new Date().toLocaleTimeString()
    }]);
  };

  const testBasicNotifications = async () => {
    const tests = [
      { type: 'success', message: 'Test success notification with audio' },
      { type: 'error', message: 'Test error notification with audio' },
      { type: 'warning', message: 'Test warning notification with audio' },
      { type: 'info', message: 'Test info notification with audio' }
    ];

    for (const test of tests) {
      try {
        await audioToast[test.type](test.message);
        addTestResult(test.type, test.message, true);
        // Wait 2 seconds between tests
        await new Promise(resolve => setTimeout(resolve, 2000));
      } catch (error) {
        addTestResult(test.type, test.message, false);
        console.error(`Error testing ${test.type}:`, error);
      }
    }
  };

  const testJobNotifications = async () => {
    const jobTests = [
      { type: 'newJob', message: 'New job available from Pool', source: 'pool' },
      { type: 'newJob', message: 'New job available from MyLink', source: 'mylink' },
      { type: 'newJob', message: 'New job available (General)', source: 'general' },
      { type: 'jobAccepted', message: 'Job accepted from Pool', source: 'pool' },
      { type: 'jobAccepted', message: 'Job accepted from MyLink', source: 'mylink' },
      { type: 'jobAccepted', message: 'Job accepted (General)', source: 'general' }
    ];

    for (const test of jobTests) {
      try {
        if (test.type === 'newJob') {
          await audioToast.newJob(test.message, test.source);
        } else {
          await audioToast.jobAccepted(test.message, test.source);
        }
        addTestResult(test.type, test.message, true);
        // Wait 2 seconds between tests
        await new Promise(resolve => setTimeout(resolve, 2000));
      } catch (error) {
        addTestResult(test.type, test.message, false);
        console.error(`Error testing ${test.type}:`, error);
      }
    }
  };

   const testDirectAudio = async () => {
     const audioTests = [
       { functionId: 'new_job_pool', name: 'New Job Pool' },
       { functionId: 'new_job_mylink', name: 'New Job MyLink' },
       { functionId: 'new_job_general', name: 'New Job General' },
       { functionId: 'job_completed', name: 'Job Completed' },
       { functionId: 'system_error', name: 'System Error' },
       { functionId: 'sleep_mode', name: 'Sleep Mode' },
       { functionId: 'running_man_start', name: 'Running Man Start (Online)' },
       { functionId: 'standing_man_stop', name: 'Standing Man Stop (Offline)' },
       { functionId: 'driver_online', name: 'Driver Online State' },
       { functionId: 'driver_offline', name: 'Driver Offline State' }
     ];

    for (const test of audioTests) {
      try {
        await audioNotificationService.playAudioForNotification('info', test.name);
        addTestResult('direct', `Direct audio test: ${test.name}`, true);
        // Wait 2 seconds between tests
        await new Promise(resolve => setTimeout(resolve, 2000));
      } catch (error) {
        addTestResult('direct', `Direct audio test: ${test.name}`, false);
        console.error(`Error testing direct audio ${test.name}:`, error);
      }
    }
  };

  const testDefaultAudio = async () => {
    const defaultTests = [
      { type: 'success', name: 'Success Default' },
      { type: 'error', name: 'Error Default' },
      { type: 'warning', name: 'Warning Default' },
      { type: 'info', name: 'Info Default' }
    ];

    for (const test of defaultTests) {
      try {
        await audioNotificationService.playDefaultAudio(test.type);
        addTestResult('default', `Default audio test: ${test.name}`, true);
        // Wait 2 seconds between tests
        await new Promise(resolve => setTimeout(resolve, 2000));
      } catch (error) {
        addTestResult('default', `Default audio test: ${test.name}`, false);
        console.error(`Error testing default audio ${test.name}:`, error);
      }
    }
  };

  const testAudioFileAccess = async () => {
    try {
      const results = await audioNotificationService.testAudioFiles();
      results.forEach(result => {
        addTestResult('accessibility', `Audio file: ${result.file}`, result.accessible);
        if (!result.accessible) {
          console.error(`Audio file not accessible: ${result.file} - ${result.error}`);
        }
      });
    } catch (error) {
      addTestResult('accessibility', 'Audio file accessibility test', false);
      console.error('Error testing audio file accessibility:', error);
    }
  };

   const testStopGoAudio = async () => {
     try {
       // Test Running Man (Go) audio
       await audioNotificationService.playStopGoAudio(true);
       addTestResult('stopgo', 'Stop/Go Audio test: Running Man (Start)', true);
       await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
       
       // Test Standing Man (Stop) audio
       await audioNotificationService.playStopGoAudio(false);
       addTestResult('stopgo', 'Stop/Go Audio test: Standing Man (Stop)', true);
       
       // Test mapped Running Man audio
       try {
         const runningManMapped = await audioNotificationService.getAudioForFunction('running_man_start');
         if (runningManMapped) {
           addTestResult('stopgo', 'Running Man mapped audio available', true);
         } else {
           addTestResult('stopgo', 'Running Man mapped audio not found', false);
         }
       } catch { }
       
       // Test mapped Standing Man audio
       try {
         const standingManMapped = await audioNotificationService.getAudioForFunction('standing_man_stop');
         if (standingManMapped) {
           addTestResult('stopgo', 'Standing Man mapped audio available', true);
         } else {
           addTestResult('stopgo', 'Standing Man mapped audio not found', false);
         }
       } catch { }
     } catch (error) {
       addTestResult('stopgo', 'Stop/Go Audio test failed', false);
       console.error('Error testing Stop/Go audio:', error);
     }
   };

  const handleAudioToggle = (enabled) => {
    setAudioEnabled(enabled);
    audioNotificationService.setEnabled(enabled);
    addTestResult('settings', `Audio notifications ${enabled ? 'enabled' : 'disabled'}`, true);
  };

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    audioNotificationService.setVolume(newVolume);
    addTestResult('settings', `Volume set to ${Math.round(newVolume * 100)}%`, true);
  };

  const clearResults = () => {
    setTestResults([]);
  };

  return (
    <div className="container mt-4">
      <Card>
        <Card.Header>
          <h3 className="mb-0">🔊 Audio Notifications Test</h3>
          <small className="text-muted">Test audio notifications with different types and sources</small>
        </Card.Header>
        <Card.Body>
          {/* Audio Settings */}
          <Row className="mb-4">
            <Col md={6}>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Audio Settings</h5>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="audioEnabled"
                      label="Enable Audio Notifications"
                      checked={audioEnabled}
                      onChange={(e) => handleAudioToggle(e.target.checked)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Volume: {Math.round(volume * 100)}%</Form.Label>
                    <Form.Range
                      min="0"
                      max="1"
                      step="0.1"
                      value={volume}
                      onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                    />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Test Controls</h5>
                </Card.Header>
                <Card.Body>
                  <div className="d-grid gap-2">
                    <Button variant="primary" onClick={testBasicNotifications}>
                      Test Basic Notifications
                    </Button>
                    <Button variant="success" onClick={testJobNotifications}>
                      Test Job Notifications
                    </Button>
                    <Button variant="info" onClick={testDirectAudio}>
                      Test Direct Audio
                    </Button>
                    <Button variant="warning" onClick={testDefaultAudio}>
                      Test Default Audio
                    </Button>
                    <Button variant="secondary" onClick={testAudioFileAccess}>
                      Test File Access
                    </Button>
                    <Button variant="primary" onClick={testStopGoAudio}>
                      Test Stop/Go Audio
                    </Button>
                    <Button variant="outline-secondary" onClick={clearResults}>
                      Clear Results
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Test Results */}
          <Card>
            <Card.Header>
              <h5 className="mb-0">Test Results</h5>
              <small className="text-muted">Results of audio notification tests</small>
            </Card.Header>
            <Card.Body>
              {testResults.length === 0 ? (
                <Alert variant="info">
                  <i className="bi bi-info-circle me-2"></i>
                  No tests run yet. Click a test button above to start testing audio notifications.
                </Alert>
              ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {testResults.map((result) => (
                    <div key={result.id} className="d-flex align-items-center mb-2 p-2 border rounded">
                      <Badge 
                        bg={result.success ? 'success' : 'danger'} 
                        className="me-2"
                      >
                        {result.success ? '✓' : '✗'}
                      </Badge>
                      <div className="flex-grow-1">
                        <strong>{result.type.toUpperCase()}:</strong> {result.message}
                      </div>
                      <small className="text-muted">{result.timestamp}</small>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Instructions */}
          <Card className="mt-4">
            <Card.Header>
              <h5 className="mb-0">Testing Instructions</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <h6>Basic Notifications</h6>
                  <ul>
                    <li>Tests success, error, warning, and info toasts with audio</li>
                    <li>Each test plays after 2-second intervals</li>
                    <li>Audio should match the notification type</li>
                  </ul>
                </Col>
                <Col md={6}>
                  <h6>Job Notifications</h6>
                  <ul>
                    <li>Tests new job notifications for different sources</li>
                    <li>Tests job accepted notifications</li>
                    <li>Audio should differentiate between Pool, MyLink, and General</li>
                  </ul>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md={6}>
                  <h6>Direct Audio</h6>
                  <ul>
                    <li>Tests audio service directly without toasts</li>
                    <li>Uses mapped audio files from database</li>
                    <li>Falls back to default audio if no mapping found</li>
                  </ul>
                </Col>
                <Col md={6}>
                  <h6>Default Audio</h6>
                  <ul>
                    <li>Tests fallback audio files</li>
                    <li>Uses NewJob_MyLink.mp3, Re-Open.mp3, etc.</li>
                    <li>Should always work regardless of mappings</li>
                  </ul>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AudioTest;
